// ─── "בקצרה" · the event in Suricata's voice, sentence by sentence sourced ───
//
// The summary comes from the `summarize` function in "cluster" mode, which
// checks its own output before caching it. The same checks run again here,
// on every read, for two reasons: the cache table accepts inserts from any
// client, and a sentence shown to the reader is a claim we make. A summary
// that fails any check is not shown; the window falls back to the report's
// own summary.
//
// The checks (DECISIONS.md, 2026-09-25):
//   · 1–5 sentences, each naming a source that is in the cluster
//   · every number in a sentence appears in the reports' titles or summaries
//   · none of the banned words · no forecast, no reading of the stock, no advice

import { useEffect, useState } from "react";
import { clusterId } from "./newsCluster";

// A Hebrew word can carry a prefix (ו, ה, ש, כ, ל, ב, מ): "ההשפעה", "שכדאי"
// and "וצפוי" are the same words, and are caught as such.
const BANNED_HE = /(^|[^֐-׿])[והשכלבמ]{0,2}(משפיע\S*|ישפיע\S*|השפע\S*|צפוי\S*|חיובי\S*|שלילי\S*|כדאי|מומלץ\S*|הזדמנו\S*|חשוב לך|אות|איתות\S*|שווה לשקול|תחזי\S*|ככל הנראה|עשוי\S*|עלול\S*)(?=$|[^֐-׿])/;
// Evaluations of an investment are not reported even with attribution: "a
// solid base for investors", "an opportunity", "good/bad for investors",
// "stocks worth watching" (David, 2026-09-26). A fact a source reported ·
// "the company reported", "analyst X raised the target to Y" · is reported;
// a verdict on whether it is worth owning is not, whoever said it.
const EVALUATIVE_HE = /(^|[^֐-׿])[והשכלבמ]{0,2}(בסיס איתן|הזדמנו\S*|טוב\S* (ל|עבור )משקיעים|רע\S* (ל|עבור )משקיעים|שכדאי|שווה לעקוב|שווה לבדוק|כדאי לעקוב|לעקוב אחרי\S*|אטרקטיבי\S*|מציאה|זולה?|זולות|זולים|יקר\S* מדי|מתומחר\S* (בחסר|ביתר)|כדאיות|המלצ\S*)(?=$|[^֐-׿])/;
const NUM_RE = /\d[\d,.]*/g;
const normNum = n => n.replace(/,/g, "").replace(/\.0+$/, "").replace(/\.$/, "");

export function checkSentences(sentences, items) {
  if (!Array.isArray(sentences) || !sentences.length || sentences.length > 5) return "count";
  const bySource = new Map();
  for (const i of items) bySource.set(i.source, `${bySource.get(i.source) || ""} ${i.title || ""} ${i.excerpt || ""}`);
  for (const s of sentences) {
    if (!s || typeof s.text !== "string" || typeof s.source !== "string") return "shape";
    if (!bySource.has(s.source) || !s.text.includes(s.source)) return "source";
    if (BANNED_HE.test(s.text)) return "banned";
    if (EVALUATIVE_HE.test(s.text)) return "evaluative";
    // Every number must appear in the report the sentence names · not just
    // somewhere in the cluster. The source's own name is taken out first, so
    // "9to5Mac" is not read as a number.
    const nums = new Set((bySource.get(s.source).match(NUM_RE) || []).map(normNum));
    for (const n of s.text.split(s.source).join(" ").match(NUM_RE) || []) if (!nums.has(normNum(n))) return "number";
  }
  return null;
}

// The sentences that pass every check, in order, at most five. A failing
// sentence is dropped, not the whole brief; if none is left the caller shows
// the report's own summary.
export function keepSentences(sentences, items) {
  if (!Array.isArray(sentences)) return [];
  return sentences.filter(s => !checkSentences([s], items)).slice(0, 5);
}

// Counted from the moment the cluster is formed, which the reader gives at
// most 2 seconds (ArticleReader, POOL_WAIT_MS): 8 seconds from opening.
export const SUMMARY_WAIT_MS = 6000;

// { state, sentences, reason }
//   "forming" · the cluster is not formed yet (items === null)
//   "loading" · a request for this cluster is in flight
//   "ready"   · sentences that passed every check
//   "none"    · no brief, with the reason: "single" (one source and a
//               summary under MIN_SINGLE_CHARS · no request is made), "timeout" (no answer within SUMMARY_WAIT_MS),
//               "failed" (the request failed) or "checks" (nothing passed).
// An answer that lands after the deadline is still used.
// Two kinds of brief:
//   · "cluster" · an event two or more outlets reported (MIN_SOURCES)
//   · "single"  · one outlet, when the opened article's own summary is long
//                 enough to be worth summarising: MIN_SINGLE_CHARS or more,
//                 and not the headline again (20/10/2026, David · the limit
//                 that kept one source out was the Gemini quota of 20 a day,
//                 and it is 450 now). Below that, no request: the window shows
//                 the report's own summary, with no note.
// The server applies the same rule (netlify/functions/summarize.js); the two
// are tested on the same cases (clusterSummary.test.js).
//
// Sprint 14, 2.4: the state is derived for the CURRENT cluster on every
// render. Before, the hook started at "loading" for any cluster, so a
// single-source one showed the "בקצרה" skeleton for a moment and then gave
// way to "מתוך הכתבה" · a brief announced and then withdrawn.
export const MIN_SOURCES = 2;
export const MIN_SINGLE_CHARS = 280;

// Letters and digits only, lower case: "Apple's Q3 — beat." and "apple s q3
// beat" are the same text.
const normText = t => String(t || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

// Whether one article's summary is long enough, and more than its headline.
export function singleEligible(a) {
  const ex = String(a?.excerpt || "").trim();
  return ex.length >= MIN_SINGLE_CHARS && normText(ex) !== normText(a?.title);
}

// What is sent, and which kind of brief it is · null when none is asked for.
export function briefRequest(items, article = null) {
  if (!items || !items.length) return null;
  if (new Set(items.map(a => a.source)).size >= MIN_SOURCES) return { kind: "cluster", items };
  const one = article || items[0];
  return singleEligible(one) ? { kind: "single", items: [one] } : null;
}

export function useClusterSummary(items, article = null) {
  const req = items ? briefRequest(items, article) : null;
  const id = req ? clusterId(req.items) : null;
  const kind = req ? req.kind : null;
  // The answer is kept with the id it answers, so a stale one is never read
  // as the current cluster's.
  const [res, setRes] = useState({ id: null, state: "loading", sentences: null, reason: null });

  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setRes({ id, state: "loading", sentences: null, reason: null });
    const timer = setTimeout(() => { if (alive) setRes(r => (r.id === id && r.state === "loading" ? { id, state: "none", sentences: null, reason: "timeout" } : r)); }, SUMMARY_WAIT_MS);
    const payload = req.items.map(a => ({ source: a.source, title: a.title, excerpt: a.excerpt || "" }));
    fetch("/.netlify/functions/summarize", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "cluster", clusterId: id, items: payload }),
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => {
        if (!alive) return;
        const kept = d ? keepSentences(d.sentences, payload) : [];
        setRes(kept.length ? { id, state: "ready", sentences: kept, reason: null } : { id, state: "none", sentences: null, reason: "checks" });
      })
      .catch(() => { if (alive) setRes({ id, state: "none", sentences: null, reason: "failed" }); });
    return () => { alive = false; clearTimeout(timer); };
    // `id` is derived from the members; the items themselves are read inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (items === null || items === undefined) return { state: "forming", sentences: null, reason: null, kind: null };
  if (!id) return { state: "none", sentences: null, reason: "single", kind: null };
  if (res.id !== id) return { state: "loading", sentences: null, reason: null, kind };
  return { ...res, kind };
}
